



































import { initializeStore, SeamlessCoreStore, Subscription } from '@seamless/store';
import VueSeamlessStore from '@seamless/vue-store-plugin';
import { Component, Prop, Ref, Vue } from 'vue-property-decorator';

import { b2xlcComponentConfig } from './interfaces/types';
import { initializeComponentRegistry } from '@owc/component-registry-connection';
import { connectionName as aemEnvConnectionName, ConnectionAemEnvironmentState } from '@owc/connection-aem-environment';
import {
  connectionName as componentRegistryConnection,
  OwcComponentRegistryState as componentRegistryState,
} from '@owc/component-registry-connection';
import {
  AUTHENTICATION_CONNECTION,
  AUTHENTICATION_CONNECTION_CIAS,
  AUTHENTICATION_CONNECTION_BIAS,
  Claims,
  Alert,
  AuthConnectionCias,
  AuthConnectionBias,
  AuthDispatchersCias,
  AuthDispatchersBias,
  CiasLogoutParams,
  CiasLoginParams,
  AuthState,
  AuthInitParams,
  JwtIssuers,
  TENANT,
  Stages,
} from '@b2x/login-library';

import i18n from './i18n/i18n';
import { Logger } from '@b2x/common';

@Component({
  i18n,
  mixins: [new VueSeamlessStore().mixin],
})
export default class App extends Vue {
  logger = new Logger('LOGIN_BUTTON');
  /**
   * Following two properties are only made for legacy header integration,
   * in Header v2 integration, they are retrieved through @owc/component-registry-connection
   */
  @Prop({ type: String, default: 'Logout' })
  dataLoggedinLabel!: string;

  @Prop({ type: String, default: 'Login' })
  dataLoggedoutLabel!: string;

  // https://pages.git.daimler.com/dh-io-mbmxp/seamless/#/guidelines/store/component-registry?id=component-registry
  @Prop({ type: String })
  componentId!: string;

  @Prop({ type: String })
  country!: string;

  @Prop({ type: String })
  language!: string;

  @Prop({ default: JwtIssuers.CIAS })
  authority!: JwtIssuers;

  @Prop({
    default: TENANT.ONEWEB,
  })
  tenantId!: string;

  @Prop()
  secondaryTenantId!: string;

  @Prop()
  ciasLogoutParams!: CiasLogoutParams;

  @Prop()
  ciasLoginParams!: CiasLoginParams;

  @Ref() readonly componentWrapper!: HTMLDivElement;

  componentRegistry!: any;

  store!: SeamlessCoreStore;

  claims: Claims | null = null;

  translations: Record<string, string> = {
    login: this.dataLoggedoutLabel,
    logout: this.dataLoggedinLabel,
  };

  ciasDispatchers!: AuthDispatchersCias;

  biasDispatchers!: AuthDispatchersBias;

  alert: Alert | null = null;

  languageDirection = 'ltr';

  userMenuButton: HTMLElement | null = null;

  async created(): Promise<void> {
    try {
      this.logger.log('login component created', this.$props);

      if (window.top?.aemNamespace) {
        this.componentRegistry = initializeComponentRegistry();
        this.componentRegistry.subscribe(this.componentId, this.onComponentConfig);
      }

      // we add both instances so that login-library consumers don't have to add it themselves
      this.logger.log('adding connections');
      await this.store.addConnection(new AuthConnectionCias()).catch(() => {
        this.logger.log('CIAS connection already added');
      });
      await this.store.addConnection(new AuthConnectionBias()).catch(() => {
        this.logger.log('BIAS connection already added');
      });

      this.ciasDispatchers = await this.store.getConnectionDispatchers(AUTHENTICATION_CONNECTION_CIAS);
      this.biasDispatchers = await this.store.getConnectionDispatchers(AUTHENTICATION_CONNECTION_BIAS);

      // This is first of two ways to initialize. When in STANDALONE or LEGACY,
      // we initialize using given attributes, and auto-detect rest (stage, market, language)
      // When in seamless ecosystem, see onAEMEvent method
      if (this.tenantId !== TENANT.ONEWEB) {
        this.logger.log('Detected STANDALONE, triggering initAuthentication()');

        this.ciasDispatchers.setInitParams(this.ciasInitParams());
        this.biasDispatchers.setInitParams(this.biasInitParams());

        this.biasDispatchers.initAuthentication();
        this.ciasDispatchers.initAuthentication();
      }

      this.store.subscribe(AUTHENTICATION_CONNECTION, this.onAuthenticationEvent);

      this.store.subscribe(aemEnvConnectionName, this.onAEMEvent);

      // legacy code cleanups
      window.localStorage.removeItem('b2x.loginInProgress');
    } catch (e) {
      this.handleAlert({ type: 'ERROR', message: e });
    }
  }

  ciasInitParams(): AuthInitParams {
    return {
      country: this.country,
      language: this.language,
      tenantId: this.authority === JwtIssuers.CIAS ? this.tenantId : this.secondaryTenantId,
      authority: JwtIssuers.CIAS,
    };
  }

  biasInitParams(): AuthInitParams {
    return {
      country: this.country,
      language: this.language,
      tenantId: this.authority === JwtIssuers.BIAS ? this.tenantId : this.secondaryTenantId,
      authority: JwtIssuers.BIAS,
    };
  }

  onLogin(): void {
    this.logger.log('onLogin called');

    try {
      // check for claims is needed as there is only one button, but it shows text for both login and logout actions,
      // We only need to call login action as logout happens in the user menu, not from this button
      if (!this.claims) {
        this.authority === JwtIssuers.CIAS
          ? this.ciasDispatchers.login(this.ciasLoginParams)
          : this.biasDispatchers.login();
      }
    } catch (e) {
      this.handleAlert({ type: 'ERROR', message: 'error.login.generic' });
    }
  }

  onLogout(): void {
    this.logger.log('onLogout called');

    try {
      if (this.authority === JwtIssuers.CIAS) {
        this.ciasDispatchers.logout(this.ciasLogoutParams);
      } else {
        this.biasDispatchers.logout();
      }
    } catch (e) {
      this.handleAlert({ type: 'ERROR', message: 'error.logout.generic' });
    }
  }

  onAuthenticationEvent(state: AuthState): void {
    this.logger.log('New auth event, isLoggedIn', state.auth.isLoggedIn, state);
    const authority = state.auth.jweIssuer;

    this.claims = this.authority === authority ? Object.freeze(state.auth.claims) : null;

    this.handleAlert(state.alert);
  }

  handleAlert(alert: Alert | null): void {
    try {
      if (alert) {
        this.logger.error('Show alert', alert);

        if (this.tenantId === TENANT.ONEWEB) {
          this.triggerUserMenuVisibility();
        }
      }

      if (alert && typeof alert?.message !== 'string') {
        alert.message = 'error.generic';
      }

      this.alert = Object.freeze(alert);
    } catch (e) {
      this.logger.error('Failed to handle alert');
    }
  }

  triggerUserMenuVisibility(): void {
    this.logger.debug('calling triggerUserMenuVisibility');

    try {
      // eslint-disable-next-line
      // @ts-ignore
      const headerShadowRoot: ShadowRoot = this.componentWrapper.parentNode.host.parentNode.shadowRoot;
      const userMenuButtonSelector = '.owc-header-mme__button';

      // eslint-disable-next-line no-undef
      const onMutation: MutationCallback = (mutations: MutationRecord[], observer: MutationObserver) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            const userMenuBtn = headerShadowRoot.querySelector(userMenuButtonSelector);

            if (userMenuBtn) {
              this.userMenuButton = userMenuBtn as HTMLElement;
              break;
            }
          }
        }

        setTimeout(() => {
          if (!this.userMenuButton?.getAttribute('open')) {
            this.userMenuButton?.click();
            this.logger.debug('triggered a click on user flyout menu');
          }
        }, 100);

        observer.disconnect();
      };

      const observer = new MutationObserver(onMutation);
      observer.observe(headerShadowRoot, { childList: true, subtree: true });
    } catch (e) {
      this.logger.log('could not open user flyout menu', e);
    }
  }

  onAlertDismiss(): void {
    this.logger.debug('dismissing alert');
    try {
      this.biasDispatchers.removeAlert();
      this.ciasDispatchers.removeAlert();
      if (this.userMenuButton?.getAttribute('open') === 'true') {
        this.logger.debug('menu is open, trying to close it by clicking');
        this.userMenuButton.click();
      }
    } catch (e) {
      this.logger.error('could not dismiss alert');
    }
  }

  async onAEMEvent(state: ConnectionAemEnvironmentState): Promise<void> {
    this.logger.log(`New ${aemEnvConnectionName} event`, state);

    this.languageDirection = state.languageDirection || this.languageDirection; // provide fallback as locally languageDirection doesn't exist

    if (state.stage && state.country && state.language) {
      i18n.locale = `${state.language}_${state.country}`;
      this.logger.log('i18n library locale changed to', i18n.locale);

      const isProtectedPage = await this.detectPageRequiresAuth();

      await this.ciasDispatchers.setInitParams({
        ...this.ciasInitParams(),
        country: state.country,
        language: state.language,
        stage: state.stage.toLowerCase() as Stages,
        isProtectedPage,
      });

      await this.biasDispatchers.setInitParams({
        ...this.biasInitParams(),
        country: state.country,
        language: state.language,
        stage: state.stage.toLowerCase() as Stages,
        isProtectedPage,
      });

      this.ciasDispatchers.initAuthentication();
      this.biasDispatchers.initAuthentication();
    }
  }

  onComponentConfig(componentConfig: b2xlcComponentConfig): void {
    this.logger.log('component config', { componentConfig });

    if (componentConfig.payload) {
      this.translations.login = componentConfig.payload.login;
      this.translations.logout = componentConfig.payload.logout;
    }
  }

  /**
   * Subscribes to the component registry and checks if any component requires login and environment is not AUTHOR
   */
  async detectPageRequiresAuth(): Promise<boolean> {
    // using this.store didn't work to subscribe to needed connections
    const store = initializeStore();
    let subscription: Subscription;

    this.logger.log('running detectPageRequiresAuth');

    const needsAuth = await new Promise<boolean>((resolve) => {
      store.once(aemEnvConnectionName, (envState: ConnectionAemEnvironmentState) => {
        let result = false;

        if (envState.runMode === 'AUTHOR') {
          return resolve(result);
        }

        subscription = store.subscribe(componentRegistryConnection, (state: componentRegistryState) => {
          const componentKeys = Object.keys(state.components);
          this.logger.debug('Components registry counter:', componentKeys?.length);

          result = componentKeys.length
            ? componentKeys.some((key) => state.components[key]?.config?.userSessionRequired)
            : false;
        });

        // Timeout was the only way for us to fulfill all components registry are done
        // Maybe seamless could not iterate but get all component object at once.
        const timeoutSettled = setTimeout(() => {
          this.logger.debug('Page requires auth:', result);

          clearTimeout(timeoutSettled);
          resolve(result);
        }, 50);
      });
    }).finally(() => {
      subscription.unsubscribe();
    });

    this.logger.log('Is auth needed: ', needsAuth);

    return needsAuth;
  }

  beforeDestroy(): void {
    this.store.unsubscribe(aemEnvConnectionName, this.onAEMEvent);
    this.store.unsubscribe(this.componentId, this.onComponentConfig);
    this.store.unsubscribe(AUTHENTICATION_CONNECTION, this.onAuthenticationEvent);
  }
}
